(ns graph-ui.web.support
  "The browser half of a fault report (PLAN-ikc-v3-039-01 §2,
   dsl/contexts/support.edn).

   TWO HANDLERS THAT DID NOT EXIST. `window.onerror` and `unhandledrejection`
   had no handler anywhere in src/cljs/graph_ui/web/. An error that did not
   crash the render left no trace a person could send. `install!` adds both,
   and each keeps the LAST error, which the next report carries.

   WHAT THE BROWSER SENDS. The person's sentence, the island, the session id,
   the URL, the DOM, the last error and the client build id. It also describes
   itself (browser-facts): the user agent, the viewport and the pointer. A screenshot only
   with consent: `getDisplayMedia` asks the person to share THIS tab
   (capture-options), and a refusal is a sentence in the report. No capture library (the plan refuses a
   new dependency).

   WHAT IT DOES NOT SEND. The steps, the time, the machine and the user. The
   handler adds them on the bus, because a browser cannot know the journal and
   must not be trusted to say who it is.

   THE FORM FOLLOWS THE ROLE, AND THE PICTURE IS TAKEN AT OPEN
   (IKC3/PLAN-ikc-v3-080-01/REPORT). A reader gets the form Tell us, and
   the team gets Report, which makes a work item. The picture is of the
   page when the person pressed Report. It can be downloaded, edited and
   uploaded again, and files of any type go with it.

   STALL, NEVER VANISH. The person writes in a dialog of this page, not in
   the browser's prompt, and the words stay until the bus says that the report
   is filed. A refused report shows the refusal under the words. A tab switch,
   a re-render or a reload does not lose them (fault report 194825ef)."
  (:require [clojure.string :as str]
            [reagent.core :as r]
            [graph-ui.web.version :as version]))

;; @plan PLAN-ikc-v3-039-01-track-b-free-the-door-support-and-the-notes
;; @plan PLAN-ikc-v3-080-01-the-tracker-v2

(defonce ^:private last-error (atom nil))

(def ^:private transport-guard
  "The most DOM characters the browser sends. It protects the socket from a
   page of many megabytes. The DECLARED limit is support.edn :limits
   :dom-characters, which the handler applies, and the report records the whole
   length."
  1000000)

(defn- error-text
  "One error as text: its message, then the first part of its stack."
  [e]
  (let [msg   (or (some-> e .-message) (str e))
        stack (some-> e .-stack str)
        ;; Chrome's stack starts with the message, so it is written once.
        text  (cond (nil? stack)                 msg
                    (str/includes? stack msg)    stack
                    :else                        (str msg "\n" stack))]
    (subs text 0 (min 4000 (count text)))))

(defn note-error!
  "Keep one error as the last error. The render-crash boundary calls this too."
  [e]
  (reset! last-error (error-text e)))

(defn install!
  "Add the two window handlers. Idempotent: a hot reload replaces the handler
   and adds no second listener."
  []
  (set! (.-onerror js/window)
        (fn [msg _src _line _col err]
          (note-error! (or err msg))
          false))
  (when-not (.-__ikcRejectionHandler js/window)
    (set! (.-__ikcRejectionHandler js/window) true)
    (.addEventListener js/window "unhandledrejection"
                       (fn [ev] (note-error! (or (.-reason ev) "an unhandled promise rejection"))))))

(def capture-options
  "What the capture asks the browser for: THIS TAB.

   IKC3-Bugs-20261003: \"The new report capture claims that it will add
   context to the report, but fails to identify the tab it is in and shows a
   list of browser tabs where the current can't be identified.\" With only
   {:video true}, Chrome opened its general picker: every tab, window and
   screen, and nothing marked the tab of the report.

   :preferCurrentTab makes Chrome offer the current tab alone, as \"Share this
   tab\". The other keys say the same thing to a browser that reads them: this
   tab is a choice, a browser tab is the surface, and no screen and no switch
   to another surface. A browser ignores a key it does not know, so Firefox
   keeps its own picker."
  {:video                {:displaySurface "browser"}
   :audio                false
   :preferCurrentTab     true
   :selfBrowserSurface   "include"
   :surfaceSwitching     "exclude"
   :monitorTypeSurfaces  "exclude"})

(defn- capture!
  "A js/Promise of {:screenshot <data URL>} or {:screenshot-absent <why>}.
   The browser asks the person to share this tab (capture-options). One frame
   is drawn to a canvas, and every track is stopped at once."
  []
  (let [md (some-> js/navigator .-mediaDevices)]
    (if-not (and md (.-getDisplayMedia md))
      (js/Promise.resolve {:screenshot-absent "This browser offers no screen capture."})
      (-> (.getDisplayMedia md (clj->js capture-options))
          (.then (fn [stream]
                   (let [video (.createElement js/document "video")
                         stop! #(doseq [t (.getTracks stream)] (.stop t))]
                     (set! (.-muted video) true)
                     (set! (.-srcObject video) stream)
                     (-> (.play video)
                         (.then (fn [] (js/Promise. (fn [res] (js/requestAnimationFrame #(res nil))))))
                         (.then (fn [_]
                                  (let [c (.createElement js/document "canvas")]
                                    (set! (.-width c) (.-videoWidth video))
                                    (set! (.-height c) (.-videoHeight video))
                                    (.drawImage (.getContext c "2d") video 0 0)
                                    (stop!)
                                    {:screenshot (.toDataURL c "image/png")})))
                         (.catch (fn [e]
                                   (stop!)
                                   {:screenshot-absent (str "The capture failed: " (.-name e) ".")}))))))
          (.catch (fn [e]
                    {:screenshot-absent
                     (if (= "NotAllowedError" (some-> e .-name))
                       "The person refused the screen capture."
                       (str "The capture did not happen: " (some-> e .-name) "."))}))))))

(defn dom-text []
  (let [h (if (exists? js/document)
            (str (some-> js/document .-documentElement .-outerHTML))
            "")]
    (if (> (count h) transport-guard) (subs h 0 transport-guard) h)))

(defn refusal-of
  "The sentence of a refused dispatch reply, or nil when the report was filed."
  [reply]
  (let [ev (if (sequential? reply) (first reply) reply)]
    (cond
      (nil? ev)                          "The bus gave no answer."
      (:rejected ev)                     (str "The bus refused the report: "
                                              (some-> (get-in ev [:rejected :reason]) name) ".")
      (= :support/error (:type ev))      (or (:message ev) "The report was not filed.")
      (:error ev)                        (str "The report was not filed: " (:error ev) ".")
      (= :support/report-filed (:type ev)) nil
      :else                              (str "The bus answered: " (pr-str ev)))))

(defn- answer-of
  "The first event of a dispatch reply."
  [reply]
  (if (sequential? reply) (first reply) reply))

(defn- refused?
  "Did the bus refuse a command, or give no answer?"
  [reply]
  (let [ev (answer-of reply)]
    (boolean (or (nil? ev) (:rejected ev) (:error ev)
                 (= :frames/error (:type ev))))))

;; ---------------------------------------------------------------------------
;; THE REPORT DIALOG — fault report 194825ef.
;;
;; George, 2026-10-03: "When I start to write fault report, if I go to another
;; tab, when I come back to continue writing - it is gone, popup closed,
;; probably not saved."
;;
;; THE CAUSE WAS THE BROWSER'S OWN PROMPT. The words were typed into
;; `js/prompt`. Chrome closes a JavaScript dialog when its tab goes to the
;; background, and a closed prompt answers nil. So the words were gone, and
;; report-fault! then filed nothing and said nothing.
;;
;; NOW THE WORDS ARE KEPT IN TWO PLACES. This `defonce` is outside every
;; component, so a tab switch, a re-render and a re-attach cannot reach it
;; (graph-ui.web.unsaved has the same reason). A copy goes to the session
;; frame with :ui/set-report-draft, so a reload gets the words back too.
;;
;; THE FORM FOLLOWS THE ROLE (IKC3/PLAN-ikc-v3-080-01/REPORT, screens 4a and
;; 4b). :support/report-form says :reader or :team, with the words of each
;; form (support.edn :forms). A reader gets Tell us. The team gets Report,
;; which makes a work item with a work type, a place, a priority and
;; acceptance criteria.
;;
;; THE PICTURE IS TAKEN WHEN REPORT OPENS (fault report 0186f4ef). Before
;; this, it was taken at File the report, so the page had changed. The
;; person can download it, draw on it with any program and upload it again
;; (fault reports 555969be and 53241b0f). A file of any type goes to the
;; :attachments vault with :ui/attach-file.
;;
;; THE KEY AND THE STATUS AFTER SEND (fault report f0a26316). The bus makes
;; the work item in the same dispatch, and the dialog reads
;; :workflow/item-of-report.
;; ---------------------------------------------------------------------------

(def ^:private empty-dialog
  ;; :phase is :writing, :capturing, :sending, :refused or :filed.
  ;; :kept says where the bus keeps the words: :session, :tab-only or nil.
  ;; :ctx is the bus, the session and the island of the open dialog.
  ;; :shot is the data URL of the picture, :shot-absent why there is none,
  ;; and :shot-at when it was taken.
  ;; :answer is the reply of :support/report-form. :filed is the work item
  ;; after Send: {:key :category-label}.
  ;; :seen is what the page was at the moment the dialog opened:
  ;; {:dom :url :last-error}. Filing sends it, not the page of the click
  ;; (fault report 0186f4ef).
  {:open? false :text "" :shot? true :phase :writing :seen nil
   :message nil :kept nil :ctx nil
   :shot nil :shot-absent nil :shot-at nil
   :answer nil :kind :wrong :effect nil :details ""
   :work-type :bug :place nil :priority nil
   :criteria [] :criterion-text "" :criterion-verifier nil
   :files [] :file-message nil :filed nil})

(defonce ^:private dialog (r/atom empty-dialog))

(def ^:private keep-delay-ms
  "How long the typing must stop before the words go to the session. One
   command per pause, not one per key."
  600)

(defonce ^:private keep-timer (atom nil))

(defn- keep-on-session!
  "Send the words now to the session frame. A blank text clears the copy.
   A refusal changes nothing on the screen except the status line: the words
   stay in this tab."
  []
  (when-let [t @keep-timer] (js/clearTimeout t) (reset! keep-timer nil))
  (let [{:keys [text ctx]}       @dialog
        {:keys [bus session-id]} ctx]
    (when (and bus session-id)
      (js-invoke bus "dispatch"
                 {:type :ui/set-report-draft :session-id session-id :text text}
                 (fn [reply]
                   (swap! dialog assoc :kept
                          (cond (refused? reply)  :tab-only
                                (str/blank? text) nil
                                :else             :session)))))))

(defn- keep-soon!
  "Send the words to the session when the typing stops."
  []
  (when-let [t @keep-timer] (js/clearTimeout t))
  (reset! keep-timer (js/setTimeout keep-on-session! keep-delay-ms)))

(defn draft-text
  "The words of the dialog now. For the tests and for a probe."
  []
  (:text @dialog))

(defn dialog-open?
  "Is the report dialog open? For the tests and for a probe."
  []
  (:open? @dialog))

(defn dialog-state
  "The whole state of the dialog. For the tests and for a probe."
  []
  @dialog)

(defn set-shot!
  "Keep the picture with the report, or remove it."
  [on?]
  (swap! dialog (fn [d] (cond-> (assoc d :shot? (boolean on?))
                          (not on?) (assoc :shot nil
                                           :shot-absent "The person removed the picture.")))))

(defn forget-tab!
  "What a reload does to this tab: the dialog and its words are gone here. The
   copy on the session stays. For the tests."
  []
  (when-let [t @keep-timer] (js/clearTimeout t) (reset! keep-timer nil))
  (reset! dialog empty-dialog))

(defn set-text!
  "The person typed. Keep the words here at once, and on the session soon."
  [text]
  (swap! dialog (fn [d]
                  (cond-> (assoc d :text (str text))
                    (#{:refused :filed} (:phase d)) (assoc :phase :writing :message nil))))
  (keep-soon!))

(defn set-field!
  "Set one field of the form: :kind, :effect, :details, :work-type, :place,
   :priority, :criterion-text or :criterion-verifier."
  [k v]
  (swap! dialog (fn [d]
                  (cond-> (assoc d k v)
                    (#{:refused :filed} (:phase d)) (assoc :phase :writing :message nil)))))

;; ---------------------------------------------------------------------------
;; The form of the caller
;; ---------------------------------------------------------------------------

(defn form-of
  "PURE: the form of a reply of :support/report-form: :team or :reader. With
   no answer the form is :reader, the form that asks for the fewest fields."
  [answer]
  (if (= :team (:form answer)) :team :reader))

(defn- words
  "The words of the form `k` of the answer: :reader, :team, :picture or
   :reopen."
  [d k]
  (get-in d [:answer :forms k]))

(defn- load-form!
  "Ask the bus which form this party gets, once per open."
  [bus]
  (js-invoke bus "dispatch" {:type :support/report-form}
             (fn [reply]
               (let [a (answer-of reply)]
                 (when (and (map? a) (:forms a))
                   (swap! dialog (fn [d]
                                   (cond-> (assoc d :answer a)
                                     (nil? (:place d))
                                     (assoc :place (get-in a [:forms :team :default-place]))
                                     (nil? (:priority d))
                                     (assoc :priority (get-in a [:forms :team :default-priority]))
                                     (nil? (:criterion-verifier d))
                                     (assoc :criterion-verifier (:default-verifier a))))))))))

;; ---------------------------------------------------------------------------
;; The picture
;; ---------------------------------------------------------------------------

(defn take-picture!
  "Take the picture of this tab now. With no capture in the browser, the
   reason is written at once, so nothing waits."
  []
  (let [md (when (exists? js/navigator) (some-> js/navigator .-mediaDevices))]
    (if-not (and md (.-getDisplayMedia md))
      (swap! dialog assoc :shot nil :shot-absent "This browser offers no screen capture.")
      (do
        (swap! dialog assoc :phase :capturing)
        (-> (capture!)
            (.then (fn [{:keys [screenshot screenshot-absent]}]
                     (swap! dialog assoc :phase :writing
                            :shot screenshot :shot-absent screenshot-absent
                            :shot? (some? screenshot)
                            :shot-at (js/Date.now)))))))))

(defn picture-file-name
  "PURE: the name of the downloaded picture, from the time it was taken."
  [at]
  (str "report-picture-"
       (-> (.toISOString (js/Date. (or at 0)))
           (subs 0 19)
           (str/replace #"[-:T]" ""))
       ".png"))

(defn- read-file!
  "Read one js File as a data URL. `on-url` gets the data URL."
  [file on-url]
  (let [rd (js/FileReader.)]
    (set! (.-onload rd) #(on-url (.-result rd)))
    (.readAsDataURL rd file)))

(defn use-picture!
  "Use an image that the person uploads as the picture of the report: the
   picture they downloaded and drew on, or another one."
  [data-url]
  (if (str/starts-with? (str data-url) "data:image/")
    (swap! dialog assoc :shot data-url :shot-absent nil :shot? true :file-message nil)
    (swap! dialog assoc :file-message "The picture must be an image. Use Add a file for another file.")))

(defn attached-file
  "PURE: the reply of :ui/attach-file as {:path :name}, or {:error <sentence>}."
  [reply nm]
  (let [a (answer-of reply)]
    (cond
      (and (map? a) (:path a)) {:path (:path a) :name (or (:name a) nm)}
      (and (map? a) (:message a)) {:error (str nm ": " (:message a))}
      :else {:error (str nm ": the bus did not store the file.")})))

(defn attach-files!
  "Send each js File to the :attachments vault with :ui/attach-file. Each
   stored file is added to `state` under :files. A refusal is a sentence in
   :file-message, and the other files go on."
  ([bus files] (attach-files! dialog bus files))
  ([state bus files]
   (doseq [f (array-seq files)]
     (let [nm (.-name f)]
       (read-file! f
                   (fn [url]
                     (js-invoke bus "dispatch" {:type :ui/attach-file :data-url url :name nm}
                                (fn [reply]
                                  (let [r (attached-file reply nm)]
                                    (if (:error r)
                                      (swap! state assoc :file-message (:error r))
                                      (swap! state (fn [d] (-> d (update :files conj r)
                                                               (assoc :file-message nil))))))))))))))

;; ---------------------------------------------------------------------------
;; Open and close
;; ---------------------------------------------------------------------------

(defn open-report!
  "Open the dialog, and take the picture of the page now, before the
   dialog shows (fault report 0186f4ef). Words already in this tab stay.
   When there are none, ask the session for the words a reload left
   behind. With :picture? false it takes no picture (+ Create)."
  [{:keys [bus session-id picture?] :or {picture? true} :as ctx}]
  (swap! dialog (fn [d]
                  (cond-> (assoc d :open? true :ctx (dissoc ctx :picture?) :shot nil
                                 ;; BEFORE the dialog is drawn: the page as it is now.
                                 :seen {:dom (dom-text)
                                        :url (if (exists? js/location) (str (.-href js/location)) "")
                                        :last-error @last-error}
                                 :shot-absent nil :shot? picture? :shot-at (js/Date.now))
                    (= :filed (:phase d)) (assoc :phase :writing :message nil :filed nil
                                                 :files [] :criteria [] :details ""))))
  (when bus (load-form! bus))
  (when picture? (take-picture!))
  (when (and bus session-id (str/blank? (:text @dialog)))
    (js-invoke bus "dispatch"
               {:type :ui/read :source :report-draft :args {:session-id session-id}}
               (fn [v]
                 ;; ONLY INTO AN EMPTY FIELD. The person can type before the
                 ;; answer comes, and an answer must not overwrite that.
                 (when (and (string? v) (not (str/blank? v))
                            (str/blank? (:text @dialog)))
                   (swap! dialog assoc :text v :kept :session))))))

(defn open-create!
  "Open the dialog from + Create of the top bar of a tracker screen
   (screen 1 of PLAN-ikc-v3-080-01). It is the form of the report, with no
   picture: a new work item does not start from a fault on the screen. The
   person can take a picture with Take a picture."
  [ctx]
  (open-report! (assoc ctx :picture? false :create? true)))

(defn close-report!
  "Close the dialog and KEEP the words. A close is not a discard."
  []
  (when @keep-timer (keep-on-session!))
  (swap! dialog assoc :open? false))

(defn discard-report!
  "Remove the words, here and on the session, and close the dialog. The only
   path that removes words the person did not file."
  []
  (swap! dialog merge {:text "" :phase :writing :message nil :details ""
                       :criteria [] :files [] :file-message nil})
  (keep-on-session!)
  (swap! dialog assoc :open? false))

;; ---------------------------------------------------------------------------
;; The browser describes itself
;; ---------------------------------------------------------------------------

(defn- pointer-kinds
  "The kinds of pointer a media feature matches, as \"coarse, fine\", or nil."
  [mm feature]
  (let [ks (filter #(.-matches (mm (str "(" feature ": " % ")"))) ["coarse" "fine"])]
    (cond (seq ks) (str/join ", " ks)
          (.-matches (mm (str "(" feature ": none)"))) "none")))

(defn browser-facts
  "PURE, given the three objects. What the browser can say about itself, for a
   fault report (support.edn :fault-report-frame). `nav` is navigator, `win`
   is window and `mm` is matchMedia. Each fact that the browser cannot give
   is left out, and an empty map means no browser.

   THE USER AGENT IS NOT ENOUGH (fault report 08e4f4c1, George on PR #166:
   \"I used (and plan to use) Firefox in both mobile or desktop mode\").
   Firefox for Android in Desktop site mode sends a desktop user agent from a
   phone. The pointer, any-pointer and the touch points say what the device
   is. Tests: T-support-web-006..007."
  [nav win mm]
  (if-not nav
    {}
    (let [uad   (.-userAgentData nav)
          hints (when uad
                  (str (str/join ", " (map #(str (.-brand %) " " (.-version %))
                                           (array-seq (or (.-brands uad) #js []))))
                       "; " (.-platform uad)
                       (when (.-mobile uad) "; mobile")))
          tp    (.-maxTouchPoints nav)
          ptr   (when mm (pointer-kinds mm "pointer"))
          any   (when mm (pointer-kinds mm "any-pointer"))]
      (cond-> {:user-agent (str (.-userAgent nav))}
        hints                              (assoc :ua-hints hints)
        (and win (.-innerWidth win))       (assoc :viewport (str (.-innerWidth win) " x " (.-innerHeight win)))
        (and win (.-devicePixelRatio win)) (assoc :device-pixel-ratio (str (.-devicePixelRatio win)))
        ptr                                (assoc :pointer ptr)
        any                                (assoc :any-pointer any)
        (number? tp)                       (assoc :max-touch-points tp)))))

(defn- this-browser
  "browser-facts of the page's own browser."
  []
  (if (exists? js/navigator)
    (browser-facts js/navigator
                   (when (exists? js/window) js/window)
                   (when (and (exists? js/window) (.-matchMedia js/window))
                     #(.matchMedia js/window %)))
    {}))

;; ---------------------------------------------------------------------------
;; Send
;; ---------------------------------------------------------------------------

(defn criteria-of
  "PURE: the criteria of the form `d`, with the text of the criterion field
   when it is not empty. A criterion written and not added is sent, never
   lost (IKC3/PLAN-ikc-v3-080-01/INT)."
  [d]
  (let [t (str/trim (str (:criterion-text d)))]
    (cond-> (vec (:criteria d))
      (not (str/blank? t)) (conj {:text t :verifier (:criterion-verifier d)}))))

(defn form-fields
  "PURE: the fields of :support/file-report that the form of `d` sends. A
   reader sends the work type of its kind, the effect and the details. The
   team also sends the place, the priority and the criteria."
  [d]
  (let [form  (form-of (:answer d))
        kinds (get-in d [:answer :forms :reader :kinds])
        kind  (first (filter #(= (:kind d) (:id %)) kinds))
        files (mapv :path (:files d))]
    (into {} (remove (fn [[_ v]] (or (nil? v) (and (coll? v) (empty? v)) (= "" v))))
          (if (= :team form)
            {:work-type (:work-type d)
             :place     (:place d)
             :priority  (:priority d)
             :criteria  (criteria-of d)
             :details   (str/trim (str (:details d)))
             :files     files}
            {:work-type (:work-type kind)
             :effect    (:effect d)
             :details   (str/trim (str (:details d)))
             :files     files}))))

(defn- shot-fields [{:keys [shot? shot shot-absent]}]
  (if (and shot? shot)
    {:screenshot shot}
    {:screenshot-absent (or shot-absent "The person declined the capture.")}))

(defn- file!
  "Send one report. `on-reply` gets the reply of the bus. `seen` is the page
   as it was when the dialog opened; the page of this moment is the fallback
   only when the open left nothing."
  [{:keys [bus session-id island what]} seen fields on-reply]
  (js-invoke bus "dispatch"
             (merge (this-browser)
                    {:type            :support/file-report
                     :what            what
                     :island          island
                     :session-id      (some-> session-id str)
                     :url             (or (:url seen) (if (exists? js/location) (str (.-href js/location)) ""))
                     :dom             (or (:dom seen) (dom-text))
                     :client-build-id version/build-id
                     :last-error      (if seen (:last-error seen) @last-error)}
                    fields)
             on-reply))

(defn filed-message
  "The sentence the person reads when the report is filed. `item` is the
   answer of :workflow/item-of-report, when it came."
  ([reply] (filed-message reply nil))
  ([reply item]
   (let [absent (:absent (answer-of reply))]
     (str (if (:key item)
            (str "Filed as " (:key item) ". Its status is " (:category-label item) ".")
            "The fault report is filed.")
          (when (seq absent)
            (str " Not in it: "
                 (str/join ", " (map (fn [[k v]] (str (name k) " (" v ")")) absent))
                 "."))))))

(defn- read-item!
  "Read the work item of the filed report, and show its key and status."
  [bus reply]
  (when-let [rid (:report-id (answer-of reply))]
    (js-invoke bus "dispatch" {:type :workflow/item-of-report :report-id rid}
               (fn [r]
                 (let [item (:item (answer-of r))]
                   (when (map? item)
                     (swap! dialog assoc :filed item :message (filed-message reply item))))))))

(defn submit-report!
  "File the words in the dialog. STALL, NEVER VANISH: the words leave the
   field only when the bus says that the report is filed. A refusal shows
   its reason under the words."
  []
  (let [{:keys [text ctx phase] :as d} @dialog
        {:keys [bus]}                  ctx]
    (when (and bus (not (str/blank? text)) (not (#{:capturing :sending} phase)))
      (swap! dialog assoc :phase :sending :message nil)
      (file! (assoc ctx :what text) (:seen d) (merge (form-fields d) (shot-fields d))
             (fn [reply]
               (if-let [why (refusal-of reply)]
                 (swap! dialog assoc :phase :refused :message why)
                 (do
                   (swap! dialog assoc :phase :filed :message (filed-message reply))
                   (read-item! bus reply)
                   ;; Words typed while the report travelled are new words,
                   ;; and they stay.
                   (when (= text (:text @dialog))
                     (swap! dialog assoc :text "" :details "" :criteria [] :criterion-text "" :files [])
                     (keep-on-session!)))))))))

(defn report-fault!
  "Open the report dialog. `crash` is the error of a render crash, when the
   crash pane opens it."
  [{:keys [crash] :as ctx}]
  (when crash (note-error! crash))
  (open-report! (select-keys ctx [:bus :session-id :island])))

;; ---------------------------------------------------------------------------
;; Drawing
;; ---------------------------------------------------------------------------

(defn- status-line
  [{:keys [phase message kept]}]
  (case phase
    :capturing "The browser asks you to share this tab."
    :sending   "The report goes to the bus."
    :refused   (str message " Your words are still here.")
    :filed     message
    (case kept
      :session  "Your words are kept on this session until you send them."
      :tab-only "Your words are kept in this tab only. The bus did not keep a copy."
      ;; A space, so the line keeps its height and nothing moves.
      " ")))

(def ^:private label-style {:fontWeight 600 :fontSize "13px" :margin "10px 0 4px"})
(def ^:private hint-style {:fontSize "12px" :color "var(--muted)"})
(def ^:private field-style
  {:width "100%" :boxSizing "border-box" :padding "8px" :fontSize "14px"
   :fontFamily "inherit" :background "var(--card)" :color "var(--text)"
   :border "1px solid var(--border)" :borderRadius "4px"})
(def ^:private button-style
  {:padding "5px 10px" :fontSize "13px" :background "transparent" :color "var(--text)"
   :border "1px solid var(--border)" :borderRadius "4px" :cursor "pointer"})

(defn- choice
  "One choice of a row of choices: a label, a hint under it, and a ring
   when it is chosen."
  [{:keys [id label hint on? on-click danger?]}]
  [:button {:data-import-id (str "report-dialog/choice-" (name id))
            :aria-pressed (boolean on?)
            :on-click on-click
            :style {:textAlign "left" :padding "6px 10px" :borderRadius "6px" :cursor "pointer"
                    :background (if on? "var(--hover)" "transparent") :color "var(--text)"
                    :border (if on? "2px solid var(--accent)" "1px solid var(--border)")}}
   [:div {:style {:fontWeight 600 :fontSize "13px" :color (when danger? "var(--crit)")}} label]
   (when hint [:div {:style hint-style} hint])])

(defn- choices
  "A row of choices of `items` for the field `k`."
  [d k items]
  (into [:div {:style {:display "flex" :flexWrap "wrap" :gap "6px"}}]
        (for [{:keys [id] :as it} items]
          ^{:key (str id)}
          [choice (assoc it :on? (= id (get d k)) :on-click #(set-field! k id))])))

(defn- picture-panel
  "The picture of the screen, and what the person can do with it."
  [d busy?]
  (let [w (merge {:label "Picture of the screen" :download "Download to draw on it"
                  :upload "Upload the edited picture" :remove "Remove the picture"
                  :take "Take a picture" :add-file "Add a file" :taken "Taken when you pressed Report."
                  :files-label "Files"}
                 (words d :picture))
        {:keys [shot shot? shot-absent shot-at files file-message ctx]} d
        has? (and shot? shot)]
    [:div {:data-import-id "report-dialog/picture"}
     [:div {:style label-style} (:label w)]
     [:div {:style {:display "flex" :gap "12px" :alignItems "flex-start" :flexWrap "wrap"}}
      (if has?
        [:img {:src shot :alt (:label w) :data-import-id "report-dialog/picture-image"
               :style {:width "160px" :maxHeight "100px" :objectFit "cover"
                       :border "1px solid var(--border)" :borderRadius "4px"}}]
        [:div {:style (merge hint-style {:width "160px" :minHeight "60px" :padding "6px"
                                         :boxSizing "border-box"
                                         :border "1px dashed var(--border)" :borderRadius "4px"})}
         (or shot-absent "No picture.")])
      [:div {:style {:display "flex" :flexDirection "column" :gap "4px"}}
       (when has?
         [:div {:style hint-style} (:taken w)])
       (when has?
         [:a {:data-import-id "report-dialog/picture-download"
              :href shot :download (picture-file-name shot-at)
              :style (merge button-style {:textDecoration "none" :display "inline-block"})}
          (:download w)])
       [:label {:data-import-id "report-dialog/picture-upload" :style (merge button-style {:display "inline-block"})}
        (:upload w)
        [:input {:type "file" :accept "image/*" :disabled busy?
                 :style {:display "none"}
                 :on-change (fn [e]
                              (when-let [f (aget (.. e -target -files) 0)]
                                (read-file! f use-picture!))
                              (set! (.. e -target -value) ""))}]]
       (if has?
         [:button {:data-import-id "report-dialog/picture-remove" :disabled busy?
                   :on-click #(set-shot! false) :style button-style}
          (:remove w)]
         [:button {:data-import-id "report-dialog/picture-take" :disabled busy?
                   :on-click #(take-picture!) :style button-style}
          (:take w)])
       [:label {:data-import-id "report-dialog/add-file" :style (merge button-style {:display "inline-block"})}
        (:add-file w)
        [:input {:type "file" :multiple true :disabled busy?
                 :style {:display "none"}
                 :on-change (fn [e]
                              (when-let [bus (:bus ctx)]
                                (attach-files! bus (.. e -target -files)))
                              (set! (.. e -target -value) ""))}]]]]
     (when (seq files)
       (into [:div {:data-import-id "report-dialog/files" :style {:marginTop "6px" :fontSize "13px"}}
              [:span {:style hint-style} (str (:files-label w) ": ")]]
             (for [{:keys [name path]} files]
               ^{:key path}
               [:span {:style {:marginRight "10px"}} name
                [:button {:title "Remove this file from the report"
                          :on-click #(swap! dialog update :files (fn [fs] (vec (remove (fn [x] (= path (:path x))) fs))))
                          :style {:marginLeft "4px" :border "none" :background "transparent"
                                  :color "var(--muted)" :cursor "pointer"}}
                 "×"]])))
     (when file-message
       [:div {:role "status" :style {:fontSize "12px" :color "var(--crit)" :marginTop "4px"}} file-message])]))

(defn- criteria-editor
  "The acceptance criteria of a new work item: each one with its verifier,
   and a field that adds one."
  [d w busy?]
  (let [vs   (or (get-in d [:answer :verifiers]) [{:value :george :label "George"}])
        lbl  (into {} (map (juxt :value :label) vs))
        ;; THE TEXT IS READ FROM THE ATOM, not from `d` of the last draw. A
        ;; click right after a keystroke came before the next draw, and the
        ;; criterion was lost (IKC3/PLAN-ikc-v3-080-01/INT).
        add! (fn []
               (swap! dialog (fn [x]
                               (let [t (str/trim (str (:criterion-text x)))]
                                 (if (str/blank? t)
                                   x
                                   (-> x
                                       (update :criteria conj {:text t :verifier (:criterion-verifier x)})
                                       (assoc :criterion-text "")))))))]
    [:div {:data-import-id "report-dialog/criteria"}
     [:div {:style label-style} (:criteria-label w)]
     [:div {:style hint-style} (:criteria-hint w)]
     (into [:div {:style {:display "flex" :flexDirection "column" :gap "2px" :margin "4px 0"}}]
           (map-indexed
            (fn [i {:keys [text verifier]}]
              ^{:key i}
              [:div {:style {:fontSize "13px"}}
               (str "✓ " text " · " (get lbl verifier (some-> verifier name)))
               [:button {:title "Remove this criterion"
                         :on-click #(swap! dialog update :criteria (fn [cs] (vec (concat (subvec cs 0 i) (subvec cs (inc i))))))
                         :style {:marginLeft "6px" :border "none" :background "transparent"
                                 :color "var(--muted)" :cursor "pointer"}}
                "×"]])
            (:criteria d)))
     [:div {:style {:display "flex" :gap "6px"}}
      [:input {:data-import-id "report-dialog/criterion-text"
               :value (:criterion-text d) :read-only busy?
               :placeholder "The page scrolls after a reconnect."
               :on-change #(set-field! :criterion-text (.. % -target -value))
               :on-key-down (fn [e] (when (= "Enter" (.-key e)) (.preventDefault e) (add!)))
               :style (merge field-style {:flex 1})}]
      (into [:select {:data-import-id "report-dialog/criterion-verifier"
                      :value (some-> (:criterion-verifier d) name)
                      :on-change #(set-field! :criterion-verifier (keyword (.. % -target -value)))
                      :style (merge field-style {:width "auto"})}]
            (for [{:keys [value label]} vs]
              ^{:key (name value)} [:option {:value (name value)} label]))
      [:button {:data-import-id "report-dialog/criterion-add" :on-click add! :style button-style} "Add"]]]))

(defn- reader-fields [d busy?]
  (let [w (words d :reader)]
    [:div
     [choices d :kind (:kinds w)]
     [:div {:style label-style} (:what-label w)]
     [:input {:data-import-id "report-dialog/text" :value (:text d) :auto-focus true
              :read-only busy? :on-change #(set-text! (.. % -target -value)) :style field-style}]
     [:div {:style label-style} (:details-label w)]
     [:textarea {:data-import-id "report-dialog/details" :value (:details d) :rows 3
                 :placeholder (:details-hint w) :read-only busy?
                 :on-change #(set-field! :details (.. % -target -value))
                 :style (merge field-style {:resize "vertical"})}]
     [:div {:style label-style} (:effect-label w)]
     [choices d :effect (:effects w)]]))

(defn- priority-label [p]
  (let [n (name p)] (str (str/upper-case (subs n 0 1)) (subs n 1))))

(defn- team-fields [d busy?]
  (let [w      (words d :team)
        labels (get-in d [:answer :work-type-labels])]
    [:div
     [:div {:style label-style} "Work type"]
     [choices d :work-type (for [t (:work-types w)] {:id t :label (get labels t (name t))})]
     [:div {:style label-style} (:summary-label w)]
     [:input {:data-import-id "report-dialog/text" :value (:text d) :auto-focus true
              :read-only busy? :on-change #(set-text! (.. % -target -value)) :style field-style}]
     [:div {:style label-style} (:details-label w)]
     [:textarea {:data-import-id "report-dialog/details" :value (:details d) :rows 3
                 :placeholder (:details-hint w) :read-only busy?
                 :on-change #(set-field! :details (.. % -target -value))
                 :style (merge field-style {:resize "vertical"})}]
     [:div {:style {:display "flex" :gap "16px" :flexWrap "wrap" :alignItems "flex-start"}}
      [:div {:style {:flex "3 1 280px"}}
       [:div {:style label-style} (:place-label w)]
       [choices d :place (map #(assoc % :danger? (= :speed-lane (:id %))) (:places w))]]
      [:div {:style {:flex "1 1 140px"}}
       [:div {:style label-style} (:priority-label w)]
       (into [:select {:data-import-id "report-dialog/priority"
                       :value (some-> (:priority d) name)
                       :on-change #(set-field! :priority (keyword (.. % -target -value)))
                       :style field-style}]
             (for [p (get-in d [:answer :priorities])]
               ^{:key (name p)} [:option {:value (name p)} (priority-label p)]))]]
     [criteria-editor d w busy?]]))

(defn report-dialog
  "The dialog in which a person writes a fault report. Drawn at the root, beside
   the island, so a crash of the island does not take it away."
  []
  (let [{:keys [open? text phase filed] :as d} @dialog
        busy? (contains? #{:capturing :sending} phase)
        form  (form-of (:answer d))
        ;; + Create has its own title, from :forms :team of support.edn.
        w     (cond-> (words d form)
                (and (get-in d [:ctx :create?]) (= :team form))
                (as-> x (assoc x :title (or (:create-title x) (:title x))
                               :subtitle (or (:create-subtitle x) (:subtitle x)))))]
    (when open?
      [:div {:data-import-id "report-dialog"
             :data-form (name form)
             :role "dialog" :aria-modal "true" :aria-label (or (:title w) "Report a fault")
             :on-key-down (fn [e]
                            (cond
                              (= "Escape" (.-key e))
                              (do (.preventDefault e) (close-report!))
                              (and (= "Enter" (.-key e)) (or (.-ctrlKey e) (.-metaKey e)))
                              (do (.preventDefault e) (submit-report!))))
             :style {:position "fixed" :inset 0 :zIndex 3000
                     :display "flex" :alignItems "center" :justifyContent "center"
                     :background "rgba(0,0,0,0.35)"
                     ;; HIDDEN WHILE THE TAB IS CAPTURED, so the picture
                     ;; shows the page and not this dialog.
                     :visibility (if (= :capturing phase) "hidden" "visible")}}
       [:div {:style {:background "var(--panel)" :color "var(--text)"
                      :border "1px solid var(--border)" :borderRadius "8px"
                      :padding "16px" :width "min(720px, calc(100vw - 32px))"
                      :maxHeight "calc(100vh - 32px)" :overflowY "auto" :boxSizing "border-box"
                      :boxShadow "0 8px 32px rgba(0,0,0,0.3)"}}
        [:div {:style {:display "flex" :alignItems "baseline" :gap "8px" :marginBottom "6px"}}
         [:span {:style {:fontWeight 600 :fontSize "16px"}} (or (:title w) "Report a fault")]
         (when (:subtitle w) [:span {:style hint-style} (:subtitle w)])]
        (if (= :team form) [team-fields d busy?] [reader-fields d busy?])
        [picture-panel d busy?]
        [:div {:data-import-id "report-dialog/status" :role "status"
               :style {:fontSize "13px" :minHeight "1.4em" :margin "10px 0 8px"
                       :color (if (= :refused phase) "var(--crit)" "var(--muted)")}}
         (status-line d)
         (when-let [k (:key filed)]
           [:a {:data-import-id "report-dialog/filed-key" :href (str "/browse/" k)
                :style {:marginLeft "6px" :color "var(--accent)"}}
            (str "Open " k)])]
        [:div {:style {:display "flex" :gap "8px" :justifyContent "flex-end" :alignItems "center"}}
         [:span {:style (merge hint-style {:marginRight "auto"})} (:footer w)]
         [:button {:data-import-id "report-dialog/discard"
                   :title "Remove these words and close"
                   :disabled busy?
                   :on-click #(discard-report!)
                   :style (merge button-style {:color "var(--muted)"})}
          "Discard"]
         [:button {:data-import-id "report-dialog/close"
                   :title "Close. Your words stay for the next time you open this."
                   :on-click #(close-report!)
                   :style button-style}
          "Close"]
         [:button {:data-import-id "report-dialog/file"
                   :title "Send the report (Ctrl+Enter)"
                   :disabled (or busy? (str/blank? text))
                   :on-click #(submit-report!)
                   :style {:padding "6px 12px" :fontSize "13px"
                           :background "var(--accent)" :color "white"
                           :border "none" :borderRadius "4px" :cursor "pointer"
                           :opacity (if (or busy? (str/blank? text)) 0.5 1)}}
          (or (:send w) "Send")]]]])))
